import { useState, useEffect, useCallback } from 'react';

// ============================================
// Types
// ============================================

interface ConnectionInfo {
  isOnline: boolean;
  effectiveType: '4g' | '3g' | '2g' | 'slow-2g' | null;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
}

interface UseOnlineStatusReturn extends ConnectionInfo {
  wasOffline: boolean;
  checkConnection: () => Promise<boolean>;
}

// Extend Navigator for Network Information API
interface NavigatorNetworkInformation extends Navigator {
  connection?: {
    effectiveType: '4g' | '3g' | '2g' | 'slow-2g';
    downlink: number;
    rtt: number;
    saveData: boolean;
    addEventListener: (type: string, listener: () => void) => void;
    removeEventListener: (type: string, listener: () => void) => void;
  };
}

// ============================================
// Hook
// ============================================

export const useOnlineStatus = (): UseOnlineStatusReturn => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<Omit<ConnectionInfo, 'isOnline'>>({
    effectiveType: null,
    downlink: null,
    rtt: null,
    saveData: false,
  });

  // Update connection info
  const updateConnectionInfo = useCallback(() => {
    const nav = navigator as NavigatorNetworkInformation;
    
    if (nav.connection) {
      setConnectionInfo({
        effectiveType: nav.connection.effectiveType,
        downlink: nav.connection.downlink,
        rtt: nav.connection.rtt,
        saveData: nav.connection.saveData,
      });
    }
  }, []);

  // Check actual connection by making a request
  const checkConnection = useCallback(async (): Promise<boolean> => {
    try {
      // Try to fetch a small resource to verify actual connectivity
      const response = await fetch('/manifest.json', {
        method: 'HEAD',
        cache: 'no-store',
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      console.log('[Network] Online');
      setIsOnline(true);
      updateConnectionInfo();
    };

    const handleOffline = () => {
      console.log('[Network] Offline');
      setIsOnline(false);
      setWasOffline(true);
    };

    // Network Information API change handler
    const handleConnectionChange = () => {
      updateConnectionInfo();
    };

    // Add listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Network Information API
    const nav = navigator as NavigatorNetworkInformation;
    if (nav.connection) {
      nav.connection.addEventListener('change', handleConnectionChange);
      updateConnectionInfo();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (nav.connection) {
        nav.connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [updateConnectionInfo]);

  return {
    isOnline,
    wasOffline,
    checkConnection,
    ...connectionInfo,
  };
};

export default useOnlineStatus;
